import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Save, Trash2, Image as ImageIcon, X, Eye, EyeOff, Calendar, Link2, Settings, FileText, Share2, Smartphone, Tablet, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import MediaPickerModal from '../components/MediaPickerModal';
import BlockEditor from '../components/BlockEditor';
import PreviewRenderer from '../components/PreviewRenderer';
import Layout from '../components/Layout';

const ArticleEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerTarget, setMediaPickerTarget] = useState('featured'); // 'featured' or 'og'
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [activeTab, setActiveTab] = useState('content');
  const [siteTheme, setSiteTheme] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  // Mobile/tablet/desktop viewport simulation for in-editor preview. Mobile
  // and tablet load `/preview/article/<token>` in an iframe so Tailwind's
  // `md:` breakpoints and the new clamp() font sizes evaluate against a
  // narrow viewport — matching the PageEditor pattern.
  const [previewViewport, setPreviewViewport] = useState('desktop');
  const [previewIframeUrl, setPreviewIframeUrl] = useState(null);
  const [iframeLoading, setIframeLoading] = useState(false);
  
  const [article, setArticle] = useState({
    title: '',
    slug: '',
    image_url: '',
    image_alt: '',
    image_fit: 'contain',
    show_image_in_feed: true,
    show_in_feed: true,
    date: new Date().toISOString().split('T')[0],
    show_date: true,
    content_html: '',
    blocks: [],
    excerpt: '',
    category: '',  // Legacy - kept for backwards compatibility
    categories: [],  // New - multiple categories
    status: 'draft',
    meta_title: '',
    meta_description: '',
    og_image: ''
  });

  useEffect(() => {
    fetchCategories();
    fetchSiteTheme();
    if (!isNew) {
      fetchArticle();
    }
  }, [id, isNew]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/articles/categories/list');
      const fetched = response.data || [];
      // 'App' is always pinned first — it controls Taptile app feed visibility
      const pinned = ['App'];
      const merged = [...pinned, ...fetched.filter(c => !pinned.includes(c))];
      setCategories(merged);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories(['App']);
    }
  };

  const fetchSiteTheme = async () => {
    try {
      const response = await api.get('/site-theme');
      setSiteTheme(response.data);
    } catch (error) {
      console.error('Failed to fetch site theme:', error);
    }
  };

  const fetchArticle = async () => {
    try {
      const response = await api.get(`/articles/${id}`);
      // Ensure blocks is an array and handle category migration
      const articleData = {
        ...response.data,
        blocks: response.data.blocks || [],
        // Migrate from single category to categories array if needed
        categories: response.data.categories || (response.data.category ? [response.data.category] : [])
      };
      setArticle(articleData);
    } catch (error) {
      toast.error('Failed to load article');
      navigate('/articles');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    setArticle(prev => ({
      ...prev,
      title: newTitle,
      // Auto-generate slug only if slug is empty or matches the auto-generated version of old title
      slug: !prev.slug || prev.slug === generateSlug(prev.title) ? generateSlug(newTitle) : prev.slug
    }));
  };

  const handleSlugChange = (e) => {
    // Allow typing but sanitize
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setArticle(prev => ({ ...prev, slug: value }));
  };

  const handleSave = async () => {
    if (!article.title.trim()) {
      toast.error('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      const saveData = {
        ...article,
        slug: article.slug || generateSlug(article.title)
      };
      
      if (isNew) {
        const response = await api.post('/articles', saveData);
        toast.success('Article created');
        navigate(`/articles/${response.data.article_id}`);
      } else {
        await api.put(`/articles/${id}`, saveData);
        toast.success('Article saved');
      }
    } catch (error) {
      console.error('Save error:', error.response?.data || error.message || error);
      toast.error(error.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await api.delete(`/articles/${id}`);
      toast.success('Article deleted');
      navigate('/articles');
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleMediaSelect = useCallback((media) => {
    if (mediaPickerTarget === 'og') {
      setArticle(prev => ({
        ...prev,
        og_image: media.url
      }));
    } else {
      setArticle(prev => ({
        ...prev,
        image_url: media.url,
        image_alt: media.filename
      }));
    }
    setShowMediaPicker(false);
  }, [mediaPickerTarget]);

  const handleAddCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      setCategories([...categories, newCategory.trim()]);
      // Add to article's categories array
      const updatedCategories = [...(article.categories || [])];
      if (!updatedCategories.includes(newCategory.trim())) {
        updatedCategories.push(newCategory.trim());
      }
      setArticle({ ...article, categories: updatedCategories, category: updatedCategories[0] || '' });
      setNewCategory('');
    }
  };

  // Delete a category entirely (with confirmation)
  const handleDeleteCategory = (categoryToDelete) => {
    if (window.confirm(`Are you sure you want to delete the category "${categoryToDelete}"?\n\nThis will remove it from all articles that use this category.`)) {
      // Remove from available categories list
      setCategories(categories.filter(c => c !== categoryToDelete));
      // Remove from current article's categories
      const updatedCategories = (article.categories || []).filter(c => c !== categoryToDelete);
      setArticle({ 
        ...article, 
        categories: updatedCategories,
        category: updatedCategories[0] || ''
      });
      toast.success(`Category "${categoryToDelete}" deleted`);
    }
  };

  // Toggle category selection
  const toggleCategory = (cat) => {
    const currentCategories = article.categories || [];
    let updatedCategories;
    if (currentCategories.includes(cat)) {
      updatedCategories = currentCategories.filter(c => c !== cat);
    } else {
      updatedCategories = [...currentCategories, cat];
    }
    // Keep legacy category field in sync (use first category)
    setArticle({ 
      ...article, 
      categories: updatedCategories,
      category: updatedCategories[0] || ''
    });
  };

  // Auto-generate excerpt from blocks
  const generateExcerpt = () => {
    // First try to get text from blocks
    for (const block of article.blocks || []) {
      if (block.type === 'paragraph' || block.type === 'text') {
        const text = block.content?.text?.replace(/<[^>]+>/g, '') || '';
        if (text) {
          return text.slice(0, 150) + (text.length > 150 ? '...' : '');
        }
      }
    }
    // Fall back to content_html for legacy articles
    const text = article.content_html?.replace(/<[^>]+>/g, '') || '';
    return text.slice(0, 150) + (text.length > 150 ? '...' : '');
  };

  const handleBlocksChange = (newBlocks) => {
    setArticle(prev => ({ ...prev, blocks: newBlocks }));
  };

  // Switch viewport. For mobile/tablet, save the article (once) and fetch a
  // preview token so the iframe shows the latest content at the simulated
  // narrow viewport. Imperative — NOT in a useEffect — so handleSave's
  // state-mutation re-renders can't cause a save loop.
  const handleSwitchViewport = async (viewport) => {
    setPreviewViewport(viewport);
    if (viewport === 'desktop') return;
    if (previewIframeUrl) return;
    if (isNew) {
      toast.error('Please save the article first to use mobile preview');
      return;
    }
    setIframeLoading(true);
    try {
      await handleSave();
      const resp = await api.get(`/articles/${id}/preview-token`);
      setPreviewIframeUrl(`${window.location.origin}/preview/article/${resp.data.preview_token}?t=${Date.now()}`);
    } catch (err) {
      console.error('Failed to build article preview URL:', err);
      toast.error('Could not load mobile preview. Try saving the article first.');
    } finally {
      setIframeLoading(false);
    }
  };

  const refreshIframePreview = async () => {
    if (isNew) {
      toast.error('Please save the article first to use mobile preview');
      return;
    }
    setPreviewIframeUrl(null);
    setIframeLoading(true);
    try {
      await handleSave();
      const resp = await api.get(`/articles/${id}/preview-token`);
      setPreviewIframeUrl(`${window.location.origin}/preview/article/${resp.data.preview_token}?t=${Date.now()}`);
    } catch (err) {
      console.error('Failed to refresh article preview:', err);
      toast.error('Could not refresh preview.');
    } finally {
      setIframeLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  // Preview Mode
  if (previewMode) {
    const viewportWidth = previewViewport === 'mobile' ? 375 : previewViewport === 'tablet' ? 768 : null;
    const viewportHeight = previewViewport === 'mobile' ? 812 : 1024;
    return (
      <Layout>
        <div className="min-h-screen bg-slate-100">
          {/* Preview Header */}
          <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
            <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="sm" onClick={() => { setPreviewMode(false); setPreviewIframeUrl(null); }}>
                  <ArrowLeft size={18} />
                </Button>
                <h1 className="text-lg font-semibold text-slate-800">Preview: {article.title}</h1>
              </div>
              <div className="flex items-center gap-3">
                {/* Viewport switcher — mobile/tablet load an iframe at that
                    width so Tailwind responsive breakpoints actually trigger. */}
                <div className="inline-flex items-center rounded-md border border-slate-300 bg-white p-1" data-testid="article-viewport-switcher">
                  <button type="button" onClick={() => handleSwitchViewport('mobile')} title="Mobile (375 px)" data-testid="article-viewport-mobile"
                    className={`p-1.5 rounded transition-colors ${previewViewport === 'mobile' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                    <Smartphone size={16} />
                  </button>
                  <button type="button" onClick={() => handleSwitchViewport('tablet')} title="Tablet (768 px)" data-testid="article-viewport-tablet"
                    className={`p-1.5 rounded transition-colors ${previewViewport === 'tablet' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                    <Tablet size={16} />
                  </button>
                  <button type="button" onClick={() => handleSwitchViewport('desktop')} title="Desktop" data-testid="article-viewport-desktop"
                    className={`p-1.5 rounded transition-colors ${previewViewport === 'desktop' ? 'bg-indigo-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                    <Monitor size={16} />
                  </button>
                </div>
                <Button variant="outline" onClick={() => { setPreviewMode(false); setPreviewIframeUrl(null); }}>
                  Exit Preview
                </Button>
              </div>
            </div>
          </div>
          
          {/* Preview Content */}
          {viewportWidth ? (
            // Mobile / tablet — render the public preview route in an iframe at
            // the simulated viewport width. This is the only way to get
            // Tailwind's `md:` and `vw` units to evaluate at phone/tablet size.
            <div className="flex flex-col items-center py-8 px-4">
              <div className="text-xs text-slate-500 mb-3 flex items-center gap-2">
                <span>{previewViewport === 'mobile' ? 'iPhone' : 'iPad'} preview · {viewportWidth} × {viewportHeight}</span>
                <button type="button" onClick={refreshIframePreview} className="underline hover:text-indigo-600" data-testid="article-preview-refresh">refresh</button>
              </div>
              <div className="bg-white shadow-xl border-[10px] border-slate-800 rounded-[36px] overflow-hidden" style={{ width: `${viewportWidth + 20}px`, maxWidth: '100%' }}>
                {iframeLoading || !previewIframeUrl ? (
                  <div className="flex items-center justify-center text-slate-400 text-sm" style={{ width: `${viewportWidth}px`, height: `${viewportHeight}px`, maxWidth: '100%' }}>
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600 mr-2"></div>
                    Loading preview…
                  </div>
                ) : (
                  <iframe
                    title="Article mobile preview"
                    src={previewIframeUrl}
                    style={{ width: `${viewportWidth}px`, height: `${viewportHeight}px`, maxWidth: '100%', border: 0, display: 'block' }}
                    data-testid="article-preview-iframe"
                  />
                )}
              </div>
            </div>
          ) : (
            // Desktop — existing inline render (unchanged).
            <div className="max-w-4xl mx-auto py-8 px-4">
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              {/* Article Header */}
              {article.image_url && (
                <img 
                  src={article.image_url} 
                  alt={article.image_alt} 
                  className={`w-full h-64 md:h-96 ${article.image_fit === 'contain' ? 'object-contain bg-slate-100' : 'object-cover'}`}
                />
              )}
              <div className="p-6 md:p-10">
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{article.title}</h1>
                {article.show_date && article.date && (
                  <p className="text-slate-500 mb-6">
                    {new Date(article.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                    {(article.categories || []).length > 0 && (
                      <span className="ml-3">
                        {(article.categories || []).map((cat, idx) => (
                          <span key={cat} className="px-2 py-1 bg-slate-100 rounded text-sm mr-1">{cat}</span>
                        ))}
                      </span>
                    )}
                  </p>
                )}
                
                {/* Block Content */}
                {article.blocks && article.blocks.length > 0 ? (
                  <PreviewRenderer 
                    blocks={article.blocks} 
                    siteTheme={siteTheme}
                    isQuickPreview={false}
                    showNavigation={false}
                  />
                ) : article.content_html ? (
                  <div 
                    className="prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: article.content_html }}
                  />
                ) : (
                  <p className="text-slate-400 italic">No content yet</p>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/articles')}>
                <ArrowLeft size={18} />
              </Button>
              <h1 className="text-lg font-semibold text-slate-800">
                {isNew ? 'New Article' : 'Edit Article'}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPreviewMode(true)}>
                <Eye size={16} className="mr-1" />
                Preview
              </Button>
              {!isNew && (
                <Button variant="ghost" size="sm" onClick={handleDelete} className="text-red-500 hover:text-red-700">
                  <Trash2 size={16} />
                </Button>
              )}
              <Select
                value={article.status}
                onValueChange={(value) => setArticle({ ...article, status: value })}
              >
                <SelectTrigger className="w-32 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">
                    <span className="flex items-center gap-2">
                      <EyeOff size={14} /> Draft
                    </span>
                  </SelectItem>
                  <SelectItem value="published">
                    <span className="flex items-center gap-2">
                      <Eye size={14} /> Published
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleSave} disabled={saving} className="gap-2">
                <Save size={16} />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="content" className="gap-2">
                <FileText size={16} />
                Content
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-2">
                <Settings size={16} />
                Settings
              </TabsTrigger>
              <TabsTrigger value="seo" className="gap-2">
                <Share2 size={16} />
                SEO & Sharing
              </TabsTrigger>
            </TabsList>

            {/* Content Tab */}
            <TabsContent value="content">
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Main Content - Block Editor */}
                <div className="lg:col-span-3 space-y-6">
                  {/* Title */}
                  <div className="bg-white rounded-lg border border-slate-200 p-4">
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Title</Label>
                    <Input
                      value={article.title}
                      onChange={handleTitleChange}
                      placeholder="Enter article title..."
                      className="text-xl font-semibold"
                      data-testid="article-title-input"
                    />
                  </div>

                  {/* Block Editor */}
                  <div className="bg-white rounded-lg border border-slate-200 p-4">
                    <Label className="text-sm font-medium text-slate-700 mb-3 block">Content</Label>
                    <BlockEditor
                      blocks={article.blocks || []}
                      onChange={handleBlocksChange}
                      placeholder="Start building your article content..."
                    />
                  </div>
                </div>

                {/* Sidebar */}
                <div className="space-y-4">
                  {/* Slug */}
                  <div className="bg-white rounded-lg border border-slate-200 p-4">
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">
                      <Link2 size={14} className="inline mr-1" />
                      URL Slug
                    </Label>
                    <Input
                      value={article.slug}
                      onChange={handleSlugChange}
                      placeholder="article-url-slug"
                      className="font-mono text-sm"
                      data-testid="article-slug-input"
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      /article/{article.slug || 'your-slug'}
                    </p>
                  </div>

                  {/* Featured Image */}
                  <div className="bg-white rounded-lg border border-slate-200 p-4">
                    <Label className="text-sm font-medium text-slate-700 mb-3 block">Featured Image</Label>
                    
                    {article.image_url ? (
                      <div className="relative">
                        <img 
                          src={article.image_url} 
                          alt={article.image_alt} 
                          className={`w-full h-40 rounded-lg ${article.image_fit === 'contain' ? 'object-contain bg-slate-100' : 'object-cover object-top'}`}
                        />
                        <div className="absolute top-2 right-2 flex gap-1">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => { setMediaPickerTarget('featured'); setShowMediaPicker(true); }}
                            className="h-7 px-2 bg-white/90 hover:bg-white text-xs"
                          >
                            Change
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setArticle({ ...article, image_url: '', image_alt: '' })}
                            className="h-7 w-7 p-0 bg-white/90 hover:bg-white text-red-500"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setMediaPickerTarget('featured'); setShowMediaPicker(true); }}
                        className="w-full h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
                        data-testid="add-featured-image-btn"
                      >
                        <ImageIcon size={24} className="text-slate-400" />
                        <span className="text-xs text-slate-500">Add featured image</span>
                      </button>
                    )}
                    
                    {article.image_url && (
                      <div className="mt-3 space-y-2">
                        <div className="flex items-center gap-3">
                          <label className="text-sm text-slate-600">Display:</label>
                          <div className="flex gap-1 bg-slate-100 rounded-md p-0.5">
                            <button
                              onClick={() => setArticle({ ...article, image_fit: 'contain' })}
                              className={`px-2.5 py-1 text-xs rounded transition-colors ${article.image_fit === 'contain' ? 'bg-white text-slate-800 shadow-sm font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                              data-testid="image-fit-contain"
                            >
                              Full Image
                            </button>
                            <button
                              onClick={() => setArticle({ ...article, image_fit: 'cover' })}
                              className={`px-2.5 py-1 text-xs rounded transition-colors ${article.image_fit === 'cover' ? 'bg-white text-slate-800 shadow-sm font-medium' : 'text-slate-500 hover:text-slate-700'}`}
                              data-testid="image-fit-cover"
                            >
                              Fill / Crop
                            </button>
                          </div>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-slate-600">
                          <input
                            type="checkbox"
                            checked={article.show_image_in_feed}
                            onChange={(e) => setArticle({ ...article, show_image_in_feed: e.target.checked })}
                            className="rounded border-slate-300"
                          />
                          Show in feed
                        </label>
                        <Input
                          placeholder="Image caption (optional)"
                          value={article.image_caption || ''}
                          onChange={(e) => setArticle({ ...article, image_caption: e.target.value })}
                          className="text-xs"
                          data-testid="featured-image-caption"
                        />
                      </div>
                    )}
                  </div>

                  {/* Preview Card */}
                  <div className="bg-white rounded-lg border border-slate-200 p-4">
                    <Label className="text-sm font-medium text-slate-700 mb-3 block">Card Preview</Label>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      {article.image_url && article.show_image_in_feed && (
                        <img 
                          src={article.image_url} 
                          alt="" 
                          className={`w-full h-24 ${article.image_fit === 'contain' ? 'object-contain bg-slate-100' : 'object-cover object-top'}`}
                        />
                      )}
                      <div className="p-3">
                        <h3 className="font-medium text-slate-800 text-sm line-clamp-2">
                          {article.title || 'Article Title'}
                        </h3>
                        {article.show_date && article.date && (
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(article.date).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 mt-2 line-clamp-2">
                          {article.excerpt || generateExcerpt() || 'Article excerpt...'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings">
              <div className="max-w-2xl space-y-6">
                {/* Date */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Article Date</Label>
                  <div className="flex items-center gap-2">
                    <Calendar size={16} className="text-slate-400" />
                    <Input
                      type="date"
                      value={article.date || ''}
                      onChange={(e) => setArticle({ ...article, date: e.target.value })}
                      className="flex-1"
                    />
                  </div>
                  <label className="flex items-center gap-2 mt-3 text-sm text-slate-600">
                    <input
                      type="checkbox"
                      checked={article.show_date}
                      onChange={(e) => setArticle({ ...article, show_date: e.target.checked })}
                      className="rounded border-slate-300"
                    />
                    Show date on article
                  </label>
                </div>

                {/* App Feed visibility */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block flex items-center gap-2">
                    App Feed
                    <span className="text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded font-medium">Taptile</span>
                  </Label>
                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={article.show_in_feed !== false}
                      onChange={(e) => setArticle({ ...article, show_in_feed: e.target.checked })}
                      className="rounded border-slate-300"
                      data-testid="article-show-in-feed"
                    />
                    Show in Taptile app feed
                  </label>
                  <p className="text-xs text-slate-400 mt-1.5">
                    When enabled, this article appears in the Taptile mobile app news feed. You can also control feed visibility using <strong>Category = "App"</strong>.
                  </p>
                </div>

                {/* Categories - Multiple Selection */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">
                    Categories
                    {(article.categories || []).length > 0 && (
                      <span className="ml-2 text-xs text-slate-400">
                        ({(article.categories || []).length} selected)
                      </span>
                    )}
                  </Label>
                  
                  {/* Selected categories badges */}
                  {(article.categories || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(article.categories || []).map((cat) => (
                        <span 
                          key={cat}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs"
                        >
                          {cat}
                          <button
                            type="button"
                            onClick={() => toggleCategory(cat)}
                            className="hover:text-indigo-900"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  
                  {/* Category checkboxes */}
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-100 rounded-lg p-2">
                    {categories.length === 0 ? (
                      <p className="text-xs text-slate-400 py-2">No categories yet. Create one below.</p>
                    ) : (
                      categories.map((cat) => (
                        <div key={cat} className="flex items-center justify-between hover:bg-slate-50 p-1 rounded group">
                          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer flex-1">
                            <input
                              type="checkbox"
                              checked={(article.categories || []).includes(cat)}
                              onChange={() => toggleCategory(cat)}
                              className="rounded border-slate-300"
                            />
                            <span>{cat}</span>
                            {cat === 'App' && (
                              <span className="text-xs bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded font-medium">Taptile</span>
                            )}
                          </label>
                          {cat !== 'App' && (
                            <button
                              type="button"
                              onClick={() => handleDeleteCategory(cat)}
                              className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 p-1 transition-opacity"
                              title={`Delete "${cat}" category`}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Add new category */}
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <Label className="text-xs text-slate-500 mb-1 block">Add new category:</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newCategory}
                        onChange={(e) => setNewCategory(e.target.value)}
                        placeholder="New category"
                        className="h-8 text-sm"
                        onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleAddCategory}
                        disabled={!newCategory.trim()}
                        className="h-8"
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Shared Article Navigation */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Article Navigation</Label>
                    <p className="text-xs text-slate-400 mt-0.5 mb-3">
                      When this article is shared or viewed directly, choose what navigation to display
                    </p>
                    <Select
                      value={article.article_nav_mode || (article.show_site_navigation === false ? 'none' : 'site_nav')}
                      onValueChange={(value) => setArticle({ ...article, article_nav_mode: value })}
                    >
                      <SelectTrigger data-testid="article-nav-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="site_nav">Use Site Navigation</SelectItem>
                        <SelectItem value="back_link">Back to Site Link</SelectItem>
                        <SelectItem value="none">Nothing</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Excerpt */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-sm font-medium text-slate-700">Excerpt (Preview Text)</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setArticle({ ...article, excerpt: generateExcerpt() })}
                      className="text-xs h-7"
                    >
                      Auto-generate
                    </Button>
                  </div>
                  <Textarea
                    value={article.excerpt || ''}
                    onChange={(e) => setArticle({ ...article, excerpt: e.target.value })}
                    placeholder="Enter a short excerpt or leave blank for auto-generated..."
                    rows={3}
                    className="resize-none"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {article.excerpt ? article.excerpt.length : 0}/150 characters (recommended)
                  </p>
                </div>
              </div>
            </TabsContent>

            {/* SEO Tab */}
            <TabsContent value="seo">
              <div className="max-w-2xl space-y-6">
                {/* Meta Title */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Meta Title</Label>
                  <Input
                    value={article.meta_title || ''}
                    onChange={(e) => setArticle({ ...article, meta_title: e.target.value })}
                    placeholder={article.title || 'Page title for search engines'}
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {(article.meta_title || article.title || '').length}/60 characters
                  </p>
                </div>

                {/* Meta Description */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <Label className="text-sm font-medium text-slate-700 mb-2 block">Meta Description</Label>
                  <Textarea
                    value={article.meta_description || ''}
                    onChange={(e) => setArticle({ ...article, meta_description: e.target.value })}
                    placeholder={article.excerpt || 'Description for search engines'}
                    rows={3}
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    {(article.meta_description || article.excerpt || '').length}/160 characters
                  </p>
                </div>

                {/* OG Image */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <Label className="text-sm font-medium text-slate-700 mb-3 block">Social Sharing Image</Label>
                  <p className="text-xs text-slate-500 mb-3">This image appears when sharing on Facebook, Twitter, LinkedIn, etc.</p>
                  
                  {(article.og_image || article.image_url) ? (
                    <div className="relative">
                      <img 
                        src={article.og_image || article.image_url} 
                        alt="Social sharing preview" 
                        className="w-full h-40 object-cover rounded-lg"
                      />
                      <div className="absolute top-2 right-2 flex gap-1">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => { setMediaPickerTarget('og'); setShowMediaPicker(true); }}
                          className="h-7 px-2 bg-white/90 hover:bg-white text-xs"
                          data-testid="article-og-image-change-btn"
                        >
                          Change
                        </Button>
                        {article.og_image && (
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => setArticle({ ...article, og_image: '' })}
                            className="h-7 w-7 p-0 bg-white/90 hover:bg-white text-red-500"
                            data-testid="article-og-image-remove-btn"
                          >
                            <X size={14} />
                          </Button>
                        )}
                      </div>
                      {!article.og_image && article.image_url && (
                        <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                          Using featured image
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => { setMediaPickerTarget('og'); setShowMediaPicker(true); }}
                      className="w-full h-32 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
                      data-testid="article-og-image-picker-btn"
                    >
                      <ImageIcon size={24} className="text-slate-400" />
                      <span className="text-xs text-slate-500">Add social image (recommended: 1200x630)</span>
                    </button>
                  )}
                </div>

                {/* Social Preview */}
                <div className="bg-white rounded-lg border border-slate-200 p-4">
                  <Label className="text-sm font-medium text-slate-700 mb-3 block">Social Preview</Label>
                  <div className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
                    {(article.og_image || article.image_url) && (
                      <img 
                        src={article.og_image || article.image_url} 
                        alt="" 
                        className="w-full h-40 object-cover"
                      />
                    )}
                    <div className="p-3">
                      <p className="text-xs text-slate-400 mb-1">yoursite.com</p>
                      <h3 className="font-semibold text-slate-800 text-sm">
                        {article.meta_title || article.title || 'Article Title'}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {article.meta_description || article.excerpt || 'Article description...'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Media Picker Modal */}
        <MediaPickerModal
          open={showMediaPicker}
          onSelect={handleMediaSelect}
          onClose={() => setShowMediaPicker(false)}
        />
      </div>
    </Layout>
  );
};

export default ArticleEditor;
